// Global Instructions Rule Applied!
//
// Per-module descriptors that drive the generic mobile browse list. Each descriptor wraps
// the EXISTING desktop list service (the same RPC-backed `/{module}/filter` endpoint the
// grids use) behind a uniform `fetchPage` adapter, declares its filter controls + default
// filters, and maps a raw row → a presentation-only `NormalizedRow`. The generic
// `useMobileList` hook + `ListScreen` consume these, so adding a module later is one
// descriptor, no new screen. We reuse services (not the desktop `use{Module}Data` hooks,
// which fetch all rows at once and are coupled to user-preference/report machinery).

import { fetchCompaniesMobileList, fetchCompaniesFilterOptions } from '../../../../app_modules/companies/services/companiesService.ts'
import { fetchDealsMobileList } from '../../../../app_modules/deals/services/dealsService.ts'
import { fetchContactsFiltered, fetchContactsFilterOptions } from '../../../../app_modules/contacts/services/contactsService.ts'
import { fetchFundsMobileList } from '../../../../app_modules/funds/services/fundsService.ts'
import { fetchFundraisingFiltered } from '../../../../app_modules/fundraising/services/fundraisingService.ts'
import {
  DEFAULT_COMPANIES_TABLE_FILTERS,
  FilterValue
} from '../../../../app_modules/global_configs/tableViewPreferences.ts'
import { LOOKUP_CATEGORY_INDUSTRY } from '../../../../state_manager/constants/lookupCategories.ts'
import { text, personName, pickFmt } from './format.ts'
import { avatarInitials } from './routes.ts'
import { MoneyFormatter } from './money.ts'
import {
  getStageOptions,
  getDefaultPipelineId,
  PIPELINE_TYPE_DEAL_ORIGINATION,
  PIPELINE_TYPE_FUNDRAISING
} from './pipelineOptions.ts'

/** A single row stat (compact key figure shown under the title). */
export interface RowStat {
  label?: string
  value: string
}

/** Presentation-only shape rendered by RecordRow (lists + recents). */
export interface NormalizedRow {
  id: string
  module: string
  title: string
  subtitle?: string
  avatarText?: string
  avatarSrc?: string
  tagText?: string
  tagColor?: string
  /** Up to ~4 compact key figures shown under the title (lists only; recents omit these). */
  stats?: RowStat[]
}

/** A selectable filter option. */
export interface FilterOption {
  value: string
  label: string
}

/**
 * Where a filter's dropdown options come from. `globalConfigs` resolves synchronously from
 * the already-loaded cfg slice (zero fetch); `fetch` calls the module's existing
 * filter-options endpoint once on mount and reads the list under `optionsKey` (used where
 * the option set is data-derived, e.g. cities or the companies a contact belongs to).
 */
export type FilterOptionSource =
  | { kind: 'globalConfigs'; select: (globalConfigs: any) => FilterOption[] }
  | {
      kind: 'fetch'
      optionsKey: string
      fetch: (token: string | null) => Promise<Record<string, Array<{ value: string | number; label: string }>>>
    }

/** A filter control offered on a list screen. All current filters are multiselect. */
export interface FilterSpec {
  /** Wire filter key the data RPC matches against. */
  key: string
  /** Short control label, e.g. "Role", "Stage", "Company". */
  label: string
  options: FilterOptionSource
}

/** Context handed to `toRow` so a module can resolve cfg-driven display + money formatting. */
export interface ListContext {
  /** cfg `pipeline_stages` keyed by id → {name, colour}; fallback for the deals stage chip. */
  stagesById?: Record<string, { name?: string; colour?: string }>
  /** Compact money formatter bound to `globalConfigs.currencies` (deals/prospects amounts). */
  formatMoney?: MoneyFormatter
}

export interface ListPageResult {
  data: any[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface ModuleListDescriptor {
  module: 'companies' | 'deals' | 'contacts' | 'funds' | 'prospects'
  /**
   * Detail-route module when it differs from `module`. Prospects browse under
   * `prospects` but their detail deep-link lives under `fundraising`
   * (`/fundraising/forms/full/:id`), matching the desktop tab module.
   */
  detailModule?: string
  /**
   * Fundraising pipeline type, set only for the fund-scoped prospects module. Its presence
   * turns on the `<FundSelect>` top selector (the funds that have prospects) and the
   * fund-resolved Stage filter; absent ⇒ no selector (deals = single pipeline, no fund scope).
   */
  pipelineType?: string
  /** Plural display label, e.g. "Companies". */
  label: string
  /** Server page size for infinite scroll. */
  pageSize: number
  /**
   * Fetch one page. Owns the per-service signature differences. `extraFilters` already
   * includes the screen's default + user-selected filters (ListScreen seeds defaults).
   * `selectionId` is the top-selector value for fund-scoped modules — the chosen Fund for
   * prospects (drives both the fund filter and the backend pipeline resolution); other
   * modules ignore it.
   */
  fetchPage: (
    page: number,
    pageSize: number,
    token: string | null,
    extraFilters?: FilterValue[],
    selectionId?: string | null
  ) => Promise<ListPageResult>
  /** Stable row identity (primary key) for keys + append-dedupe. */
  rowId: (row: any) => string
  /** Map a raw row → NormalizedRow for display. */
  toRow: (row: any, ctx?: ListContext) => NormalizedRow
  /** Filter controls offered for this module (rendered in the FilterBar). */
  filters?: FilterSpec[]
  /**
   * When a module's filter options depend on the selected pipeline (prospects' Stage
   * is scoped to the chosen Fundraising pipeline), recompute the specs for `pipelineId`.
   * ListScreen prefers this over the static `filters` when present.
   */
  filtersFor?: (pipelineId: string | null) => FilterSpec[]
  /** Filters applied by default (seeded into the UI selection, so the control reflects them). */
  defaultFilters?: FilterValue[]
}

/** Build a stats array, dropping entries with no value. */
const statList = (...items: Array<RowStat | null | undefined | false>): RowStat[] =>
  items.filter((s): s is RowStat => !!s && !!s.value)

/** Industry options from the `Industry` lookup header in globalConfigs (zero fetch). */
const industryOptions = (gc: any): FilterOption[] => {
  const header = (gc?.lookup_headers ?? []).find((h: any) => h?.category === LOOKUP_CATEGORY_INDUSTRY)
  const details = header?.details ?? []
  return [...details]
    .sort((a: any, b: any) => (a?.sort_order || 0) - (b?.sort_order || 0))
    .map((d: any) => ({ value: String(d.id), label: String(d.label ?? '') }))
    .filter((o: FilterOption) => o.label)
}

// Shared one-time filter-options fetchers (module-level so useFilterOptions can dedupe by
// reference — companies' Country + City both resolve from a single companies fetch).
const fetchCompanyFilterOptions = (token: string | null) => fetchCompaniesFilterOptions(token).then((r) => r.data)
const fetchContactFilterOptions = (token: string | null) => fetchContactsFilterOptions(token).then((r) => r.data)

const companies: ModuleListDescriptor = {
  module: 'companies',
  label: 'Companies',
  pageSize: 25,
  // Mobile uses the lean /accounts/mobile-list read (id + name + investment stats only),
  // not the full grid RPC — ~4–5x faster, ~12x smaller payload. extra carries the
  // Role/Industry/Country/City filters the RPC applies server-side.
  fetchPage: (page, pageSize, token, extra) => fetchCompaniesMobileList(extra ?? [], page, pageSize, token),
  rowId: (r) => String(r.accounts__id),
  toRow: (r) => {
    const inv = r?.prog__investment_stats
    return {
      id: String(r.accounts__id),
      module: 'companies',
      title: text(r.accounts__name),
      avatarText: avatarInitials(r.accounts__name),
      // MOIC reads as another investment statistic, inline after Unrlzd (same
      // label+value style as the figures before it, not a coloured tag).
      stats: statList(
        inv?.cost_basis_fmt ? { label: 'Cost', value: String(inv.cost_basis_fmt) } : null,
        inv?.realized_value_fmt ? { label: 'Rlzd', value: String(inv.realized_value_fmt) } : null,
        inv?.unrealized_value_fmt ? { label: 'Unrlzd', value: String(inv.unrealized_value_fmt) } : null,
        inv?.moic_fmt ? { label: 'MOIC', value: String(inv.moic_fmt) } : null
      )
    }
  },
  // Companies default to Role=Portfolio (matches the desktop grid's default scope); seeded
  // into the UI selection so the Role control reflects it.
  defaultFilters: DEFAULT_COMPANIES_TABLE_FILTERS,
  filters: [
    {
      key: 'account_roles__account_role_choice_id',
      label: 'Role',
      options: {
        kind: 'globalConfigs',
        select: (gc) =>
          (gc?.account_role_choices ?? []).map((c: any) => ({ value: String(c.id), label: String(c.role ?? '') }))
      }
    },
    {
      key: 'accounts__industry',
      label: 'Industry',
      options: { kind: 'globalConfigs', select: (gc) => industryOptions(gc) }
    },
    {
      key: 'accounts__business_country_code',
      label: 'Country',
      options: { kind: 'fetch', optionsKey: 'accounts__business_country_code', fetch: fetchCompanyFilterOptions }
    },
    {
      key: 'accounts__business_city_code',
      label: 'City',
      options: { kind: 'fetch', optionsKey: 'accounts__business_city_code', fetch: fetchCompanyFilterOptions }
    }
  ]
}

const deals: ModuleListDescriptor = {
  module: 'deals',
  label: 'Deals',
  pageSize: 25,
  // Mobile uses the lean /deals/mobile-list read (base + stage + Val/raise/commitment),
  // not the full grid RPC — ~40x faster (drops the tasks/contacts laterals), tiny payload.
  // The Stage filter rides through `extra` (pipeline_deals__stage) into the RPC.
  fetchPage: (page, pageSize, token, extra) => fetchDealsMobileList(extra ?? [], page, pageSize, token),
  rowId: (r) => String(r.deals__id),
  toRow: (r, ctx) => {
    const stageId = r.pipeline_deals__stage
    const cfg = stageId && ctx?.stagesById ? ctx.stagesById[String(stageId)] : undefined
    const tagText = r.pipeline_deals__stage__label || cfg?.name
    const tagColor = r.pipeline_stages__colour || cfg?.colour
    const fm = ctx?.formatMoney
    const cur = r.deals__currency
    const vt = String(r.prog__valuation_type || '')
    // Pre/Post qualifier goes AFTER the value (e.g. "$14.0M Post").
    const valSuffix = /^pre/i.test(vt) ? ' Pre' : /^post/i.test(vt) ? ' Post' : ''
    // Raise foregrounded with the valuation after "@" (e.g. "Raising $10.0M @ $150.0M Post").
    // Valuation-only deals fall back to the bare "Val" figure; both-missing drops the stat.
    let stats: RowStat[] | undefined
    if (fm) {
      const hasRaise = r.rounds__raise_amount != null
      const hasVal = r.prog__valuation != null
      const valStr = hasVal ? `${fm(r.prog__valuation, cur)}${valSuffix}` : ''
      const raiseStr = hasRaise ? fm(r.rounds__raise_amount, cur) : ''
      const raiseVal: RowStat | null = hasRaise
        ? { label: 'Raising', value: hasVal ? `${raiseStr} @ ${valStr}` : raiseStr }
        : hasVal
          ? { label: 'Val', value: valStr }
          : null
      stats = statList(
        raiseVal,
        r.prog__commitment_amount != null ? { label: 'Commit', value: fm(r.prog__commitment_amount, cur) } : null
      )
    }
    return {
      id: String(r.deals__id),
      module: 'deals',
      // Deal name usually carries the company, so we drop the company subtitle on mobile.
      title: text(r.deals__name),
      avatarText: avatarInitials(r.deals__name || r.accounts__name),
      tagText: tagText ? String(tagText) : undefined,
      tagColor: tagColor ? String(tagColor) : undefined,
      stats
    }
  },
  filters: [
    {
      key: 'pipeline_deals__stage',
      label: 'Stage',
      // Stages are constrained to the single Deal Origination pipeline via pipeline_config.
      options: {
        kind: 'globalConfigs',
        select: (gc) =>
          getStageOptions(gc, getDefaultPipelineId(gc, PIPELINE_TYPE_DEAL_ORIGINATION), 'tbl').map((s) => ({
            value: s.value,
            label: s.label
          }))
      }
    }
  ]
}

const contacts: ModuleListDescriptor = {
  module: 'contacts',
  label: 'Contacts',
  pageSize: 25,
  fetchPage: (page, pageSize, token, extra) => fetchContactsFiltered([], extra ?? [], page, pageSize, token),
  rowId: (r) => String(r.contacts__id),
  toRow: (r) => {
    const name = personName(r.contacts__first_name, r.contacts__last_name, r.contacts__preferred_name, r.contacts__email)
    return {
      id: String(r.contacts__id),
      module: 'contacts',
      title: name,
      subtitle: r.contact_accounts__title ? String(r.contact_accounts__title) : undefined,
      avatarText: avatarInitials(name),
      avatarSrc: r.contacts__photo_url || undefined
    }
  },
  filters: [
    {
      key: 'contact_accounts__account_id',
      label: 'Company',
      options: { kind: 'fetch', optionsKey: 'contact_accounts__account_id', fetch: fetchContactFilterOptions }
    }
  ]
}

const funds: ModuleListDescriptor = {
  module: 'funds',
  label: 'Funds',
  pageSize: 50,
  // Mobile uses the lean /funds/mobile-list read (id + name + closed/FMV/MOIC only),
  // not the full grid RPC — ~5–6x faster (skips cashflows/IRR), tiny payload. Funds has
  // no mobile filters, so extra/pipelineId are unused.
  fetchPage: (page, pageSize, token) => fetchFundsMobileList(page, pageSize, token),
  rowId: (r) => String(r.funds__id),
  toRow: (r) => {
    // The funds tablegrid row exposes closed capital (not committed) + FMV + MOIC top-level.
    const closed = pickFmt(r, ['funds__closed_capital_fmt'])
    const fmv = pickFmt(r, ['fmv_fmt'])
    const moic = pickFmt(r, ['moic_fmt'])
    return {
      id: String(r.funds__id),
      module: 'funds',
      title: text(r.funds__name),
      avatarText: avatarInitials(r.funds__name),
      stats: statList(
        closed ? { label: 'Closed', value: closed } : null,
        fmv ? { label: 'FMV', value: fmv } : null,
        moic ? { label: 'MOIC', value: moic } : null
      )
    }
  }
}

/**
 * Prospects' Stage filter, scoped to a Fundraising pipeline. Unlike deals (one fixed
 * pipeline), prospects can switch pipeline, so the stage options must be recomputed for
 * the selected pipeline id — falling back to the default Fundraising pipeline when none
 * is chosen yet. Returns the single-spec array `filtersFor` / `filters` expect.
 */
const prospectStageFilter = (pipelineId: string | null): FilterSpec[] => [
  {
    key: 'pipeline_prospects__stage',
    label: 'Stage',
    options: {
      kind: 'globalConfigs',
      select: (gc) =>
        getStageOptions(gc, pipelineId ?? getDefaultPipelineId(gc, PIPELINE_TYPE_FUNDRAISING), 'tbl').map((s) => ({
          value: s.value,
          label: s.label
        }))
    }
  }
]

const prospects: ModuleListDescriptor = {
  module: 'prospects',
  // Prospect detail deep-links live under the desktop `fundraising` tab module.
  detailModule: 'fundraising',
  pipelineType: PIPELINE_TYPE_FUNDRAISING,
  label: 'Prospects',
  pageSize: 25,
  // The selector is a FUND (not a pipeline): send it as a `prospects__fund` filter and use
  // the `pipeline_board` context, so the backend both scopes prospects to that fund and
  // resolves the fund's Fundraising pipeline (get_fund_pipeline_origination + default
  // fallback). The frontend mirrors that resolution for the Stage filter (filtersFor).
  fetchPage: (page, pageSize, token, extra, fundId) =>
    fetchFundraisingFiltered(
      [],
      fundId ? [...(extra ?? []), { key: 'prospects__fund', value: [fundId] }] : extra ?? [],
      page,
      pageSize,
      token,
      null,
      'pipeline_board'
    ),
  rowId: (r) => String(r.prospects__id),
  toRow: (r, ctx) => {
    const stageLabel = r.pipeline_prospects__stage__label
    const stageColour = r.pipeline_stages__colour
    const fm = ctx?.formatMoney
    const cur = r.prospects__currency
    return {
      id: String(r.prospects__id),
      module: 'prospects',
      // Investor (account) name headlines; stage is the chip. The fund is no longer
      // shown per-row — the screen is scoped to a single fund via the Fund selector.
      title: text(r.accounts__name),
      avatarText: avatarInitials(r.accounts__name),
      tagText: stageLabel ? String(stageLabel) : undefined,
      tagColor: stageColour ? String(stageColour) : undefined,
      stats:
        fm && r.prospects__commitment != null
          ? statList({ label: 'Commitment', value: fm(r.prospects__commitment, cur) })
          : undefined
    }
  },
  // Default-pipeline stage options for any direct reader; ListScreen uses `filtersFor`
  // to re-scope these to the actively selected pipeline.
  filters: prospectStageFilter(null),
  filtersFor: prospectStageFilter
}

const DESCRIPTORS: Record<string, ModuleListDescriptor> = { companies, deals, contacts, funds, prospects }

/** The browse-hub order (and the set of modules that have a mobile list). */
export const BROWSE_MODULES: ModuleListDescriptor['module'][] = ['companies', 'deals', 'contacts', 'funds', 'prospects']

/** Look up a descriptor by route module param; null for unknown/desktop-only modules. */
export function getDescriptor(module: string | undefined | null): ModuleListDescriptor | null {
  return module ? DESCRIPTORS[module] ?? null : null
}
